import { useState } from "react";

import { useFileDispute } from "./api";
import { readApiErrorMessage } from "./errors";
import { formatMoneyCents } from "./format";

// Phase 5c portal-dispute-button. Modal that lets a customer file a
// §11 invoice dispute within the 15-day window. Validates reason +
// explanation client-side (mirrors the server enum + 30..4000 length
// rule on `Dispute`) so users don't round-trip a 422 to learn the
// rules. Server errors (window_closed, already_open) still surface
// inline via readApiErrorMessage.

const REASON_OPTIONS = [
  { value: "unauthorized_charge",    label: "I didn't authorize this charge" },
  { value: "incorrect_amount",       label: "The amount is wrong" },
  { value: "equipment_not_received", label: "I never received the equipment" },
  { value: "equipment_defective",    label: "Equipment arrived defective or unsafe" },
  { value: "service_charge_disputed", label: "A line item (cleaning, fuel, damage) is wrong" },
  { value: "other",                  label: "Something else" }
] as const;

const EXPLANATION_MIN = 30;
const EXPLANATION_MAX = 4000;

interface InvoiceContext {
  invoice_number: string;
  total_cents: number;
  dispute_window_ends_at: string | null;
}

interface Props {
  invoice: InvoiceContext;
  onClose: () => void;
  onFiled?: () => void;
}

export function DisputeDialog({ invoice, onClose, onFiled }: Props): JSX.Element {
  const [reason, setReason] = useState<string>("");
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useFileDispute(invoice.invoice_number);

  function handleSubmit(): void {
    setError(null);
    if (!reason) {
      setError("Pick a reason so we can route this to the right person.");
      return;
    }
    const trimmed = explanation.trim();
    if (trimmed.length < EXPLANATION_MIN) {
      setError(`Add at least ${EXPLANATION_MIN} characters so we have enough to investigate.`);
      return;
    }
    submit.mutate(
      { reason, explanation: trimmed },
      {
        onSuccess: () => {
          onFiled?.();
          onClose();
        },
        onError: (e: Error) => setError(readApiErrorMessage(e))
      }
    );
  }

  const remaining = EXPLANATION_MAX - explanation.length;
  const windowDeadline = formatDeadline(invoice.dispute_window_ends_at);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dispute-dialog-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      data-testid="dispute-dialog"
    >
      <div className="w-full max-w-lg space-y-4 rounded-t-xl bg-brand-white p-5 shadow-xl sm:rounded-xl">
        <header className="space-y-1.5 border-b border-zinc-100 pb-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
            Dispute charge
          </p>
          <h2 id="dispute-dialog-title"
              className="font-serif text-xl font-bold italic tracking-tight text-brand-black">
            Invoice {invoice.invoice_number}
          </h2>
          <p className="text-xs text-zinc-600">
            {formatMoneyCents(invoice.total_cents)}
            {windowDeadline ? ` · You have until ${windowDeadline} to dispute.` : ""}
          </p>
          <span aria-hidden="true" className="inline-block h-0.5 w-8 bg-brand-red" />
        </header>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-brand-black">What&rsquo;s the issue?</legend>
          {REASON_OPTIONS.map((opt) => (
            <label key={opt.value}
                   className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md px-1 text-sm
                              hover:bg-zinc-50 has-[:checked]:text-brand-black">
              <input type="radio"
                     name="dispute-reason"
                     value={opt.value}
                     checked={reason === opt.value}
                     onChange={() => setReason(opt.value)}
                     className="h-4 w-4 accent-brand-red"
                     data-testid={`dispute-reason-${opt.value}`} />
              <span>{opt.label}</span>
            </label>
          ))}
        </fieldset>

        <label className="block space-y-1">
          <span className="text-sm font-semibold text-brand-black">
            Tell us what happened
          </span>
          <textarea rows={5}
                    value={explanation}
                    maxLength={EXPLANATION_MAX}
                    onChange={(e) => setExplanation(e.target.value)}
                    placeholder="Dates, line items, what we got wrong — anything that helps us look into it."
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm
                               focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red"
                    data-testid="dispute-explanation" />
          <p className="flex justify-between text-xs">
            <span className={explanation.trim().length < EXPLANATION_MIN ? "text-zinc-500" : "text-emerald-700"}>
              Min {EXPLANATION_MIN} characters.
            </span>
            <span className="text-zinc-500">{remaining} left</span>
          </p>
        </label>

        {error ? (
          <p className="rounded-md border border-red-600 bg-red-50 p-2 text-sm text-red-700"
             role="alert"
             data-testid="dispute-error">
            {error}
          </p>
        ) : null}

        <p className="text-xs text-zinc-500">
          We&rsquo;ll review and follow up by phone or email within a few business days.
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button"
                  onClick={onClose}
                  className="min-h-[44px] rounded-md border border-zinc-300 px-4 py-2 text-sm font-semibold text-brand-black hover:bg-zinc-50"
                  data-testid="dispute-cancel">
            Cancel
          </button>
          <button type="button"
                  onClick={handleSubmit}
                  disabled={submit.isPending}
                  className="min-h-[44px] rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white transition hover:bg-brand-red-dark disabled:opacity-40"
                  data-testid="dispute-submit">
            {submit.isPending ? "Filing…" : "File dispute"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDeadline(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}
